<template>
  <div
    data-feedback-ui
    data-test="feedback-panel"
    role="dialog"
    aria-modal="false"
    :aria-label="t('feedback.title')"
    class="fixed bottom-4 right-4 z-toast flex max-h-[85vh] w-[min(26rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-theme-border bg-theme-surface-raised shadow-theme-elevated"
    @paste="handlePaste"
  >
    <header class="flex items-start justify-between gap-3 border-b border-theme-border px-4 py-3">
      <div class="min-w-0">
        <h2 class="text-sm font-semibold text-theme-text">{{ t('feedback.title') }}</h2>
        <p class="mt-0.5 text-xs text-theme-text-secondary">{{ t('feedback.description') }}</p>
      </div>
      <button
        type="button"
        class="shrink-0 rounded-theme-sm p-1 text-theme-text-secondary transition hover:bg-theme-bg-subtle hover:text-theme-text"
        :aria-label="t('feedback.cancel')"
        data-test="feedback-close"
        @click="closePanel()"
      >
        <icon :path="mdiClose" size="16" />
      </button>
    </header>

    <div class="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
      <div v-if="lastReference" class="rounded-theme-sm border border-theme-success/40 bg-theme-success/10 px-3 py-2">
        <p class="text-xs font-semibold text-theme-success">{{ t('feedback.success') }}</p>
        <p class="mt-0.5 text-xs text-theme-text-secondary">
          {{ t('feedback.reference', { reference: lastReference }) }}
        </p>
        <router-link
          v-if="hasMyFeedback"
          :to="{ name: 'my-feedback' }"
          class="mt-1 inline-block text-xs font-medium text-theme-palette-emerald underline-offset-2 hover:underline"
          data-test="feedback-my-feedback-link"
          @click="closePanel()"
        >
          {{ t('feedback.mine.open') }}
        </router-link>
      </div>

      <!-- Context first, then what kind of thing it is, then the prose. This
           is the order people actually report in: point at the thing, say
           whether it is broken or an idea, then explain. -->
      <section class="space-y-2">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
          {{ t('feedback.context_title') }}
        </h3>

        <div class="rounded-theme-sm border border-theme-border px-3 py-2">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="text-xs font-medium text-theme-text">{{ t('feedback.selected_text') }}</p>
              <p v-if="draft.selectedText" class="mt-0.5 line-clamp-3 text-xs text-theme-text-secondary">
                “{{ draft.selectedText }}”
              </p>
              <p v-else class="mt-0.5 text-xs text-theme-text-muted">{{ t('feedback.selected_text_empty') }}</p>
            </div>
            <div class="flex shrink-0 gap-1">
              <app-button
                v-if="hasSelectionAvailable"
                size="xs"
                variant="ghost"
                tone="primary"
                :label="t('feedback.use_selection')"
                data-test="feedback-use-selection"
                @click="useCurrentSelection()"
              />
              <app-button
                v-if="draft.selectedText"
                size="xs"
                variant="ghost"
                tone="muted"
                :label="t('feedback.remove')"
                @click="clearSelectedText()"
              />
            </div>
          </div>
        </div>

        <div class="rounded-theme-sm border border-theme-border px-3 py-2">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="text-xs font-medium text-theme-text">{{ t('feedback.element') }}</p>
              <p v-if="draft.element" class="mt-0.5 truncate text-xs text-theme-text-secondary">
                {{ draft.element.label || draft.element.test_id || draft.element.role }}
              </p>
              <p v-else class="mt-0.5 text-xs text-theme-text-muted">{{ t('feedback.element_empty') }}</p>
            </div>
            <div class="flex shrink-0 gap-1">
              <app-button
                size="xs"
                variant="ghost"
                tone="primary"
                :label="draft.element ? t('feedback.pick_again') : t('feedback.pick_element')"
                data-test="feedback-pick-element"
                @click="startPicking()"
              />
              <app-button
                v-if="draft.element"
                size="xs"
                variant="ghost"
                tone="muted"
                :label="t('feedback.remove')"
                @click="clearElement()"
              />
            </div>
          </div>
        </div>
      </section>

      <div class="flex gap-1">
        <app-button
          v-for="option in kindOptions"
          :key="option.value"
          size="xs"
          :variant="draft.kind === option.value ? '' : 'outline'"
          :tone="draft.kind === option.value ? 'primary' : 'muted'"
          :label="option.label"
          @click="draft.kind = option.value"
        />
      </div>

      <app-textarea
        v-model="draft.description"
        :label="t('feedback.message')"
        :placeholder="t('feedback.message_placeholder')"
        :rows="4"
        size="sm"
        required
        data-test="feedback-description"
        container-class="mb-0"
      />

      <!-- Screenshots -->
      <section class="space-y-2">
        <div class="flex items-center justify-between">
          <h3 class="text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
            {{ t('feedback.screenshots') }}
          </h3>
          <span class="text-xs text-theme-text-muted">{{ draft.screenshots.length }}/{{ maxScreenshots }}</span>
        </div>

        <!-- One bordered target that says what it is and offers the three ways
             in. It accepts a drop as well as a paste, because a box that looks
             droppable and is not reads as broken. -->
        <div
          class="rounded-theme-sm border border-dashed px-3 py-3 text-center transition-colors"
          :class="
            dropActive
              ? 'border-theme-focus bg-theme-focus/5'
              : 'border-theme-border bg-theme-bg-subtle/40'
          "
          data-test="feedback-screenshot-dropzone"
          @dragover.prevent="dropActive = true"
          @dragleave.prevent="dropActive = false"
          @drop.prevent="onDrop"
        >
          <p class="text-xs text-theme-text-secondary">{{ t('feedback.paste_hint') }}</p>
          <div class="mt-2 flex flex-wrap justify-center gap-1">
            <app-button
              v-if="captureSupported"
              size="xs"
              variant="outline"
              tone="muted"
              :icon="mdiSelectionDrag"
              :label="t('feedback.capture_region')"
              :disabled="atScreenshotLimit"
              data-test="feedback-capture-region"
              @click="captureRegion()"
            />
            <app-button
              v-if="captureSupported"
              size="xs"
              variant="outline"
              tone="muted"
              :icon="mdiMonitorScreenshot"
              :label="t('feedback.capture')"
              :disabled="atScreenshotLimit"
              data-test="feedback-capture"
              @click="captureScreenshot()"
            />
            <app-button
              size="xs"
              variant="outline"
              tone="muted"
              :icon="mdiUpload"
              :label="t('feedback.upload')"
              :disabled="atScreenshotLimit"
              data-test="feedback-upload"
              @click="fileInput?.click()"
            />
          </div>
          <input
            ref="fileInput"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            class="sr-only"
            :aria-label="t('feedback.upload')"
            @change="onFilePicked"
          />
        </div>

        <ul v-if="draft.screenshots.length" class="space-y-2">
          <li
            v-for="(shot, index) in draft.screenshots"
            :key="shot.id"
            class="rounded-theme-sm border border-theme-border px-3 py-2"
          >
            <div class="flex items-center justify-between gap-2">
              <span class="text-xs font-medium text-theme-text">
                {{ t('feedback.screenshot_n', { index: index + 1 }) }}
              </span>
              <div class="flex gap-1">
                <app-button
                  size="xs"
                  variant="ghost"
                  tone="primary"
                  :icon="mdiPencilOutline"
                  :label="t('feedback.annotate.edit')"
                  data-test="feedback-edit-screenshot"
                  @click="editScreenshot(shot.id)"
                />
                <app-button
                  size="xs"
                  variant="ghost"
                  tone="muted"
                  :icon="mdiTrashCanOutline"
                  :label="t('feedback.remove')"
                  @click="removeScreenshot(shot.id)"
                />
              </div>
            </div>
                      </li>
        </ul>
      </section>

      <div v-if="error" role="alert" data-test="feedback-error">
        <p class="text-xs text-theme-danger">{{ errorMessage }}</p>
        <!-- A denied screen-capture permission is remembered by the browser
             per site, and nothing this app can call will un-deny it. Saying
             where the switch is beats silently failing every time after. -->
        <p v-if="error === 'capture_cancelled'" class="mt-1 text-xs text-theme-text-secondary">
          {{ t('feedback.errors.capture_blocked_hint') }}
        </p>
      </div>
    </div>

    <footer class="flex items-center justify-between gap-2 border-t border-theme-border px-4 py-3">
      <router-link
        v-if="hasMyFeedback"
        :to="{ name: 'my-feedback' }"
        class="text-xs text-theme-text-secondary underline-offset-2 hover:text-theme-text hover:underline"
        data-test="feedback-my-feedback-footer-link"
        @click="closePanel()"
      >
        {{ t('feedback.mine.open') }}
      </router-link>
      <div class="flex items-center gap-2">
        <app-button variant="outline" tone="muted" size="sm" :label="t('feedback.cancel')" @click="closePanel()" />
        <app-button
          size="sm"
          tone="primary"
          :label="t('feedback.submit')"
          :loading="submitting"
          :disabled="!canSubmit"
          data-test="feedback-submit"
          @click="$emit('submit')"
        />
      </div>
    </footer>
  </div>
</template>

<script setup>
  /**
   * The reporting panel. Deliberately NOT an `AppModal`: a modal traps focus
   * and puts a backdrop over the page, which would make "select text on the
   * page while writing the report" impossible. It is a floating,
   * non-modal panel instead, marked `data-feedback-ui` so the element picker
   * and the capture step can exclude it.
   */
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';
  import {
    mdiClose,
    mdiMonitorScreenshot,
    mdiPencilOutline,
    mdiSelectionDrag,
    mdiTrashCanOutline,
    mdiUpload,
  } from '@mdi/js';

  import Icon from '@/components/Icon';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import AppTextarea from '@/components/ui/forms/AppTextarea.vue';
  import {
    MAX_SCREENSHOTS,
    useFeedbackReporter,
  } from '@/features/feedback/composables/useFeedbackReporter';

  const emit = defineEmits(['submit']);
  const { t } = useI18n();
  // The admin app mounts this panel too, but has no "my feedback" page to link to.
  const hasMyFeedback = useRouter().hasRoute('my-feedback');
  const fileInput = ref(null);

  // The draft and every action live in the module-level reporter composable,
  // so the panel holds no copy of them: closing it, picking an element or a
  // failed submit all leave the same objects in place.
  const {
    draft,
    submitting,
    error,
    lastReference,
    canSubmit,
    captureSupported,
    hasSelectionAvailable,
    closePanel,
    useCurrentSelection,
    clearSelectedText,
    startPicking,
    clearElement,
    captureScreenshot,
    captureRegion,
    addScreenshotFile,
    handlePaste,
    removeScreenshot,
    editScreenshot,
  } = useFeedbackReporter();

  const maxScreenshots = MAX_SCREENSHOTS;
  const dropActive = ref(false);

  const kindOptions = computed(() => [
    { value: 'issue', label: t('feedback.kind_issue') },
    { value: 'suggestion', label: t('feedback.kind_suggestion') },
  ]);
  const atScreenshotLimit = computed(() => draft.screenshots.length >= MAX_SCREENSHOTS);
  const errorMessage = computed(() => (error.value ? t(`feedback.errors.${error.value}`) : ''));

  function onFilePicked(event) {
    const file = event.target.files?.[0];
    if (file) addScreenshotFile(file);
    event.target.value = '';
  }

  function onDrop(event) {
    dropActive.value = false;
    const file = Array.from(event.dataTransfer?.files || []).find((entry) =>
      entry.type?.startsWith('image/')
    );
    if (file) addScreenshotFile(file);
  }
</script>
